import { useState, useMemo, useCallback, useEffect } from 'react';
import type React from 'react';
import type { Category } from '@/modelos/productos/Producto';
import type { Unit } from '@/modelos/Unidad';
import type { Establecimiento } from '@/modelos/Establecimiento';
import type { Product, ProductFormData } from '@/modelos/productos/Producto';
import type { ProductInput } from './useProductoStore';
import { archivosService } from '@/services/api/archivos.service';
import type { ArchivoDto } from '@/modelos/Archivo';
// ── Barcode helpers ──────────────────────────────────────────────────────────
const BARCODE_MIN_LENGTH = 8;
const BARCODE_MAX_LENGTH = 14;
function normalizeBarcodeValue(v?: string | null): string {
  if (v === null || v === undefined) return '';
  return v.trim().replace(/\s+/g, '').replace(/[^\x20-\x7E]/g, '');
}
function isBarcodeValueValid(value: string): boolean {
  const n = normalizeBarcodeValue(value);
  return Boolean(n) && n.length >= BARCODE_MIN_LENGTH && n.length <= BARCODE_MAX_LENGTH;
}
export function generateRandomProductCode(): string {
  return Math.random().toString(36).substring(2, 10).toUpperCase();
}
export function generateRandomBarcode(): string {
  return Math.floor(Math.random() * 10_000_000_000).toString().padStart(10, '0');
}
type UnitFamily = Unit['category'];

export type ProductType = 'BIEN' | 'SERVICIO';

export type FormError = {
  [K in keyof ProductFormData]?: string;
} & {
  establecimientoIds?: string;
  defaultTaxLabel?: string;
};

export type AdditionalUnitError = { unidad?: string; factor?: string };

interface UseProductFormParams {
  isOpen: boolean;
  product?: Product;
  categories: Category[];
  availableUnits: Unit[];
  allProducts: Product[];
  activeEstablecimientos: Establecimiento[];
  defaultEstablecimientoId?: string;
  isFieldVisible: (fieldId: string) => boolean;
  isFieldRequired: (fieldId: string) => boolean;
  onSave: (productData: ProductInput) => Product | Promise<Product | void> | void;
  onClose: () => void;
  defaultTaxLabel?: string;
  prefillName?: string;
}

const buildDefaultFormData = (
  defaultUnit: Product['unidad'],
  defaultUnitId: string | undefined,
  defaultUnitSymbol: string | undefined,
  defaultUnitName: string | undefined,
  defaultCategoryName: string,
  defaultEstablecimientoIds: string[],
  defaultTaxLabel?: string
): ProductFormData => ({
  nombre: '',
  codigo: generateRandomProductCode(),
  unidad: defaultUnit,
  unidadMedidaId: defaultUnitId,
  unidadSymbol: defaultUnitSymbol,
  unidadName: defaultUnitName,
  unidadesMedidaAdicionales: [],
  categoria: defaultCategoryName,
  impuesto: defaultTaxLabel ?? '',
  descripcion: '',
  establecimientoIds: defaultEstablecimientoIds,
  disponibleEnTodos: false,
  alias: '',
  precioCompra: 0,
  porcentajeGanancia: 0,
  codigoBarras: '',
  codigoFabrica: '',
  codigoSunat: '',
  codigoSunatNombre: '',
  descuentoProducto: 0,
  marca: '',
  modelo: '',
  peso: 0,
  tipoExistencia: 'MERCADERIAS'
});

export const useProductForm = ({
  isOpen,
  defaultTaxLabel,
  product,
  availableUnits,
  allProducts,
  activeEstablecimientos,
  defaultEstablecimientoId,
  isFieldVisible,
  isFieldRequired,
  onSave,
  prefillName
}: UseProductFormParams) => {
  const resolveFamilyLabel = useCallback((family: UnitFamily): string => {
    switch (family) {
      case 'SERVICIOS':
        return 'Servicios';
      case 'TIME':
        return 'Tiempos';
      case 'WEIGHT':
        return 'Pesos';
      case 'VOLUME':
        return 'Volúmenes';
      case 'LENGTH':
        return 'Longitudes';
      case 'AREA':
        return 'Áreas';
      case 'ENERGY':
        return 'Energías';
      case 'QUANTITY':
        return 'Cantidades';
      case 'PACKAGING':
        return 'Empaques';
      default:
        return family;
    }
  }, []);

  const resolveUnitSnapshot = useCallback(
    (code?: string) => {
      const normalized = (code ?? '').trim();
      if (!normalized) {
        return { id: undefined, symbol: undefined, name: undefined, description: undefined };
      }
      const unit = availableUnits.find(u => u.code === normalized);
      return {
        id: unit?.id,
        symbol: unit?.symbol,
        name: unit?.name,
        description: unit?.description
      };
    },
    [availableUnits]
  );
  const activeEstablecimientoIds = useMemo(
    () => activeEstablecimientos.filter(est => est.estaActivoEstablecimiento !== false).map(est => est.id),
    [activeEstablecimientos]
  );

  const resolvedDefaultEstablecimientoId = useMemo(() => {
    if (defaultEstablecimientoId && activeEstablecimientoIds.includes(defaultEstablecimientoId)) {
      return defaultEstablecimientoId;
    }
    const main = activeEstablecimientos.find(
      est => est.estaActivoEstablecimiento !== false && est.isMainEstablecimiento
    );
    return main?.id ?? activeEstablecimientoIds[0] ?? '';
  }, [activeEstablecimientos, activeEstablecimientoIds, defaultEstablecimientoId]);

  const uniqueIds = useCallback((ids: string[]) => {
    const set = new Set<string>();
    ids.forEach(id => {
      const trimmed = String(id || '').trim();
      if (trimmed) {
        set.add(trimmed);
      }
    });
    return Array.from(set);
  }, []);

  const resolveDefaultEnabledEstablecimientos = useCallback((): string[] => {
    if (activeEstablecimientoIds.length === 1) {
      return [activeEstablecimientoIds[0]];
    }
    if (resolvedDefaultEstablecimientoId) {
      return [resolvedDefaultEstablecimientoId];
    }
    if (activeEstablecimientoIds.length > 0) {
      return [activeEstablecimientoIds[0]];
    }
    return [];
  }, [activeEstablecimientoIds, resolvedDefaultEstablecimientoId]);

  const sortedUnits = useMemo(() => {
    return [...availableUnits].sort((a, b) => {
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [availableUnits]);

  const visibleUnits = useMemo(() => {
    return availableUnits.filter(unit => unit.isActive !== false && unit.isVisible !== false);
  }, [availableUnits]);

  const sortedVisibleUnits = useMemo(() => {
    return [...visibleUnits].sort((a, b) => {
      const displayDelta = (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
      if (displayDelta !== 0) return displayDelta;
      return a.name.localeCompare(b.name);
    });
  }, [visibleUnits]);

  const defaultCategoryName = '';

  const getDefaultUnitForType = useCallback(
    (type: ProductType): Product['unidad'] => {
      if (availableUnits.length === 0) {
        return '' as Product['unidad'];
      }

      if (type === 'SERVICIO') {
        const serviceUnits = availableUnits.filter(unit => unit.category === 'SERVICIOS');
        const serviceVisible = serviceUnits.filter(unit => unit.isActive !== false && unit.isVisible !== false);
        const pool = serviceVisible.length ? serviceVisible : serviceUnits;
        const preferredZZ = pool.find(unit => unit.code === 'ZZ') ?? serviceUnits.find(unit => unit.code === 'ZZ');
        if (preferredZZ) return preferredZZ.code as Product['unidad'];
        const defaultService = pool.find(unit => unit.isDefault) ?? pool[0];
        if (defaultService) return defaultService.code as Product['unidad'];
      }

      const nonServiceUnits = availableUnits.filter(unit => unit.category !== 'SERVICIOS');
      const quantityUnits = nonServiceUnits.filter(unit => unit.category === 'QUANTITY');
      const quantityVisible = quantityUnits.filter(unit => unit.isActive !== false && unit.isVisible !== false);
      const quantityPool = quantityVisible.length ? quantityVisible : quantityUnits;

      if (quantityPool.length > 0) {
        const preferredNIU = quantityPool.find(unit => unit.code === 'NIU') ?? quantityUnits.find(unit => unit.code === 'NIU');
        if (preferredNIU) return preferredNIU.code as Product['unidad'];

        const favoriteQuantity = quantityPool.find(unit => unit.isFavorite) ?? quantityUnits.find(unit => unit.isFavorite);
        if (favoriteQuantity) return favoriteQuantity.code as Product['unidad'];

        const defaultQuantity = quantityPool.find(unit => unit.isDefault) ?? quantityPool[0];
        if (defaultQuantity) return defaultQuantity.code as Product['unidad'];
      }

      const nonServiceVisible = nonServiceUnits.filter(unit => unit.isActive !== false && unit.isVisible !== false);
      const pool = nonServiceVisible.length ? nonServiceVisible : nonServiceUnits;

      const favoriteUnit = pool.find(unit => unit.isFavorite) ?? nonServiceUnits.find(unit => unit.isFavorite);
      if (favoriteUnit) return favoriteUnit.code as Product['unidad'];

      const defaultUnit = pool.find(unit => unit.isDefault) ?? pool[0];
      if (defaultUnit) return defaultUnit.code as Product['unidad'];

      return '' as Product['unidad'];
    },
    [availableUnits]
  );

  const resolveFamilyFromCode = useCallback(
    (code?: string): UnitFamily => {
      const normalized = (code ?? '').trim();
      if (!normalized) {
        return availableUnits[0]?.category ?? 'QUANTITY';
      }
      const unit = availableUnits.find(u => u.code === normalized);
      return unit?.category ?? availableUnits[0]?.category ?? 'QUANTITY';
    },
    [availableUnits]
  );

  const initialUnit = getDefaultUnitForType('BIEN');
  const initialUnitSnapshot = resolveUnitSnapshot(initialUnit);

  const [formData, setFormData] = useState<ProductFormData>(
    () =>
      buildDefaultFormData(
        initialUnit,
        initialUnitSnapshot.id,
        initialUnitSnapshot.symbol,
        initialUnitSnapshot.name,
        defaultCategoryName,
        resolveDefaultEnabledEstablecimientos(),
        defaultTaxLabel
      )
  );
  const [productType, setProductType] = useState<ProductType>('BIEN');
  const [selectedUnitFamily, setSelectedUnitFamily] = useState<UnitFamily>('QUANTITY');
  const unitFamilies = useMemo(() => {
    // Orden deseado: Servicios, Tiempos, Pesos, Volúmenes, Longitudes, Áreas, Energías, Cantidades, Empaques
    const desiredOrder: UnitFamily[] = [
      'SERVICIOS',
      'TIME',
      'WEIGHT',
      'VOLUME',
      'LENGTH',
      'AREA',
      'ENERGY',
      'QUANTITY',
      'PACKAGING'
    ];

    // Obtener familias que tienen al menos una unidad visible o es la seleccionada
    const seen = new Set<UnitFamily>();
    const ordered = [...sortedUnits].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const availableFamilies = ordered.map(unit => unit.category).filter(category => {
      if (seen.has(category)) return false;
      const hasVisible = visibleUnits.some(u => u.category === category);
      const isSelected = category === selectedUnitFamily;
      if (!hasVisible && !isSelected) return false;
      seen.add(category);
      return true;
    });

    // Si no hay familias disponibles, usar todas las categorías de unidades visibles
    const families = availableFamilies.length 
      ? availableFamilies 
      : Array.from(new Set(visibleUnits.map(unit => unit.category)));

    // Ordenar según el orden deseado
    return families.sort((a, b) => {
      const indexA = desiredOrder.indexOf(a);
      const indexB = desiredOrder.indexOf(b);
      const sortA = indexA === -1 ? 999 : indexA;
      const sortB = indexB === -1 ? 999 : indexB;
      return sortA - sortB;
    });
  }, [selectedUnitFamily, sortedUnits, visibleUnits]);
  const [errors, setErrors] = useState<FormError>({});
  const [additionalUnitErrors, setAdditionalUnitErrors] = useState<AdditionalUnitError[]>([]);
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [pendingImageFiles, setPendingImageFiles] = useState<File[]>([]);
  const [archivosExistentes, setArchivosExistentes] = useState<ArchivoDto[]>([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [unitInfoMessage, setUnitInfoMessage] = useState<string | null>(null);
  const [hasInitializedForm, setHasInitializedForm] = useState(false);

  const filteredUnitsForFamily = useMemo(() => {
    return sortedVisibleUnits.filter(unit => unit.category === selectedUnitFamily);
  }, [sortedVisibleUnits, selectedUnitFamily]);

  const baseUnitOptions = useMemo(() => {
    // Always show only units from the selected family (strict — no fallback to all units)
    const familyUnits = filteredUnitsForFamily;
    const selected = availableUnits.find(unit => unit.code === formData.unidad);
    // If the currently-selected unit belongs to a different family, prepend it
    // so the <select> never shows a blank/mismatched value
    if (selected && !familyUnits.some(u => u.code === selected.code)) {
      return [selected, ...familyUnits];
    }
    return familyUnits;
  }, [filteredUnitsForFamily, availableUnits, formData.unidad]);

  const isUsingFallbackUnits = filteredUnitsForFamily.length === 0 && sortedVisibleUnits.length > 0;

  const remainingUnitsForAdditional = useMemo(() => {
    const usedCodes = new Set<string>([
      formData.unidad,
      ...formData.unidadesMedidaAdicionales.map(unit => unit.unidadCodigo)
    ]);
    const allowedOptions = filteredUnitsForFamily.length > 0 ? filteredUnitsForFamily : sortedVisibleUnits;
    return allowedOptions.filter(unit => !usedCodes.has(unit.code));
  }, [formData.unidad, formData.unidadesMedidaAdicionales, filteredUnitsForFamily, sortedVisibleUnits]);

  const findUnitByCode = useCallback(
    (code?: string) => {
      if (!code) return undefined;
      return sortedUnits.find(unit => unit.code === code);
    },
    [sortedUnits]
  );
  const formatFactorValue = useCallback((value?: number) => {
    if (!value) return '0';
    const formatter = new Intl.NumberFormat('es-PE', {
      maximumFractionDigits: 4,
      minimumFractionDigits: value % 1 === 0 ? 0 : 2
    });
    return formatter.format(value);
  }, []);

  const handleUnitFamilyChange = useCallback(
    (nextFamily: UnitFamily) => {
      if (selectedUnitFamily === nextFamily) return;

      const previousAdditionalCount = formData.unidadesMedidaAdicionales.length;
      let sanitizedUnits: ProductFormData['unidadesMedidaAdicionales'] = [];

      // Primero cambia la selección de chip.
      setSelectedUnitFamily(nextFamily);

      setFormData(prev => {
        const visibleForFamily = sortedVisibleUnits.filter(unit => unit.category === nextFamily);
        const hasUnitsForFamily = visibleForFamily.length > 0;
        const availableForFamily = hasUnitsForFamily ? visibleForFamily : sortedVisibleUnits;

        const currentUnit = availableUnits.find(unit => unit.code === prev.unidad);
        const currentUnitValid = currentUnit ? currentUnit.category === nextFamily : false;

        let nextUnit = prev.unidad as Product['unidad'];

        if (!currentUnitValid) {
          const prioritized =
            availableForFamily.find(unit => unit.isDefault) ??
            availableForFamily.find(unit => unit.isFavorite) ??
            availableForFamily[0];
          nextUnit = (prioritized?.code || '') as Product['unidad'];
        }

        // Limpiamos presentaciones que coinciden con la nueva unidad mínima o que no están dentro de la familia.
        sanitizedUnits = prev.unidadesMedidaAdicionales.filter(unit => {
          if (unit.unidadCodigo === nextUnit) return false;
          if (!unit.unidadCodigo) return false;
          if (!hasUnitsForFamily) return false;
          const resolved = availableUnits.find(item => item.code === unit.unidadCodigo);
          if (!resolved) return true;
          return resolved.category === nextFamily;
        });

        const snapshot = resolveUnitSnapshot(nextUnit);
        return {
          ...prev,
          unidad: nextUnit,
          unidadMedidaId: snapshot.id,
          unidadSymbol: snapshot.symbol,
          unidadName: snapshot.name,
          unidadMedidaDescripcion: snapshot.description,
          unidadesMedidaAdicionales: sanitizedUnits
        };
      });

      setAdditionalUnitErrors(sanitizedUnits.map(() => ({})));
      setErrors(prev => ({
        ...prev,
        unidad: undefined
      }));

      if (sanitizedUnits.length < previousAdditionalCount) {
        setUnitInfoMessage(
          `Se limpiaron presentaciones que no son compatibles con la familia ${resolveFamilyLabel(nextFamily)}.`
        );
      }
    },
    [formData.unidadesMedidaAdicionales, selectedUnitFamily, sortedVisibleUnits, availableUnits, resolveUnitSnapshot, resolveFamilyLabel]
  );

  const handleBaseUnitChange = useCallback(
    (nextUnit: Product['unidad']) => {
      let removedByBaseChange = false;
      const snapshot = resolveUnitSnapshot(nextUnit);
      setFormData(prev => {
        const filteredAdditional = prev.unidadesMedidaAdicionales.filter(unit => unit.unidadCodigo !== nextUnit);
        if (filteredAdditional.length !== prev.unidadesMedidaAdicionales.length) {
          removedByBaseChange = true;
          setAdditionalUnitErrors(previousErrors => {
            const filteredErrors: AdditionalUnitError[] = [];
            prev.unidadesMedidaAdicionales.forEach((unit, idx) => {
              if (unit.unidadCodigo !== nextUnit) {
                filteredErrors.push(previousErrors[idx] || {});
              }
            });
            return filteredErrors;
          });
        }

        return {
          ...prev,
          unidad: nextUnit,
          unidadMedidaId: snapshot.id,
          unidadSymbol: snapshot.symbol,
          unidadName: snapshot.name,
          unidadMedidaDescripcion: snapshot.description,
          unidadesMedidaAdicionales: filteredAdditional
        };
      });

      if (removedByBaseChange) {
        setUnitInfoMessage('Se limpiaron presentaciones que coincidían con la nueva unidad mínima.');
      }
    },
    [resolveUnitSnapshot]
  );

  const addAdditionalUnit = useCallback(() => {
    if (remainingUnitsForAdditional.length === 0) return;
    setFormData(prev => ({
      ...prev,
      unidadesMedidaAdicionales: [
        ...prev.unidadesMedidaAdicionales,
        {
          unidadCodigo: '',
          factorConversion: 1,
          unidadSymbol: undefined,
          unidadName: undefined
        }
      ]
    }));
    setAdditionalUnitErrors(prev => [...prev, {}]);
  }, [remainingUnitsForAdditional, resolveUnitSnapshot]);

  const removeAdditionalUnit = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      unidadesMedidaAdicionales: prev.unidadesMedidaAdicionales.filter((_, i) => i !== index)
    }));
    setAdditionalUnitErrors(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateAdditionalUnit = useCallback(
    (index: number, field: 'nombre' | 'unidadCodigo' | 'factorConversion', value: string) => {
      setFormData(prev => ({
        ...prev,
        unidadesMedidaAdicionales: prev.unidadesMedidaAdicionales.map((unit, i) =>
          i === index
            ? (() => {
                if (field === 'nombre') {
                  return { ...unit, nombre: value };
                }
                if (field === 'factorConversion') {
                  return {
                    ...unit,
                    factorConversion: Number(value)
                  };
                }
                const snapshot = resolveUnitSnapshot(value);
                return {
                  ...unit,
                  unidadCodigo: value,
                  unidadSymbol: snapshot.symbol,
                  unidadName: snapshot.name
                };
              })()
            : unit
        )
      }));

      setAdditionalUnitErrors(prev => {
        const next = [...prev];
        const target = { ...(next[index] || {}) };
        if (field === 'factorConversion') {
          delete target.factor;
        } else if (field === 'unidadCodigo') {
          delete target.unidad;
        }
        next[index] = target;
        return next;
      });
    },
    [resolveUnitSnapshot]
  );

  const getAdditionalUnitOptions = useCallback(
    (rowIndex: number) => {
      const allowedOptions = filteredUnitsForFamily.length > 0 ? filteredUnitsForFamily : sortedVisibleUnits;
      return allowedOptions.filter(unit => {
        if (unit.code === formData.unidad) return false;
        return (
          unit.code === formData.unidadesMedidaAdicionales[rowIndex]?.unidadCodigo ||
          !formData.unidadesMedidaAdicionales.some((other, idx) => idx !== rowIndex && other.unidadCodigo === unit.code)
        );
      });
    },
    [filteredUnitsForFamily, sortedVisibleUnits, formData.unidad, formData.unidadesMedidaAdicionales]
  );

  useEffect(() => {
    setAdditionalUnitErrors(prev => {
      if (prev.length === formData.unidadesMedidaAdicionales.length) {
        return prev;
      }
      return formData.unidadesMedidaAdicionales.map((_, index) => prev[index] || {});
    });
  }, [formData.unidadesMedidaAdicionales]);

  useEffect(() => {
    if (availableUnits.length === 0) return;
    setFormData(prev => {
      if (!prev.unidad) return prev;
      const unit = availableUnits.find(u => u.code === prev.unidad);
      if (!unit) return prev;
      if (prev.unidadMedidaId && prev.unidadSymbol && prev.unidadName) return prev;
      return {
        ...prev,
        unidadMedidaId: prev.unidadMedidaId || unit.id,
        unidadSymbol: prev.unidadSymbol || unit.symbol,
        unidadName: prev.unidadName || unit.name,
        unidadMedidaDescripcion: prev.unidadMedidaDescripcion || unit.description,
      };
    });
  }, [availableUnits]);

  const handleProductTypeChange = useCallback(
    (nextType: ProductType) => {
      setProductType(nextType);
      const defaultUnit = getDefaultUnitForType(nextType);
      setSelectedUnitFamily(resolveFamilyFromCode(defaultUnit));
      const snapshot = resolveUnitSnapshot(defaultUnit);
      setFormData(prev => ({
        ...prev,
        unidad: defaultUnit,
        unidadMedidaId: snapshot.id,
        unidadSymbol: snapshot.symbol,
        unidadName: snapshot.name,
        unidadMedidaDescripcion: snapshot.description,
        unidadesMedidaAdicionales: [],
        tipoExistencia: nextType === 'SERVICIO' ? 'SERVICIOS' : 'MERCADERIAS'
      }));
      setAdditionalUnitErrors([]);
    },
    [getDefaultUnitForType, resolveFamilyFromCode, resolveUnitSnapshot]
  );

  const initializeFormForNewProduct = useCallback(() => {
    const defaultUnit = getDefaultUnitForType('BIEN');
    setSelectedUnitFamily('QUANTITY');
    const snapshot = resolveUnitSnapshot(defaultUnit);
    const nextFormData = buildDefaultFormData(
      defaultUnit,
      snapshot.id,
      snapshot.symbol,
      snapshot.name,
      defaultCategoryName,
      resolveDefaultEnabledEstablecimientos(),
      defaultTaxLabel
    );
    const trimmedPrefill = prefillName?.trim() ?? '';
    if (trimmedPrefill) {
      nextFormData.nombre = trimmedPrefill;
    }
    setFormData(nextFormData);
    setImagePreview('');
    setPendingImageFiles([]);
    setProductType('BIEN');
    setAdditionalUnitErrors([]);
    setErrors({});
    setUnitInfoMessage(null);
    setIsDescriptionExpanded(false);
  }, [
    defaultTaxLabel,
    getDefaultUnitForType,
    prefillName,
    resolveDefaultEnabledEstablecimientos,
    resolveUnitSnapshot
  ]);

  const initializeFormFromProduct = useCallback(
    (productData: Product) => {
      const additionalUnits =
        productData.unidadesMedidaAdicionales?.map(unit => ({
          nombre: unit.nombre,
          unidadCodigo: unit.unidadCodigo,
          factorConversion: unit.factorConversion,
          unidadSymbol: unit.unidadSymbol,
          unidadName: unit.unidadName
        })) || [];
      const inferredFamily = resolveFamilyFromCode(productData.unidad);
      const sanitizedUnits = additionalUnits.filter(unit => {
        if (!unit.unidadCodigo) return false;
        if (unit.unidadCodigo === productData.unidad) return false;
        return true;
      });

      const enabledIdsFromProduct = productData.disponibleEnTodos
        ? activeEstablecimientoIds
        : uniqueIds(productData.establecimientoIds || []);

      const finalEstablecimientoIds = enabledIdsFromProduct.length > 0
        ? enabledIdsFromProduct
        : resolveDefaultEnabledEstablecimientos();

      const unitSnapshot = resolveUnitSnapshot(productData.unidad);

      setFormData({
        nombre: productData.nombre,
        codigo: productData.codigo,
        tipoProducto: productData.tipoProducto,
        unidad: productData.unidad,
        unidadMedidaId: productData.unidadMedidaId ?? unitSnapshot.id,
        unidadSymbol: productData.unitSymbol ?? unitSnapshot.symbol,
        unidadName: productData.unitName ?? unitSnapshot.name,
        unidadMedidaDescripcion: productData.unidadMedidaDescripcion ?? unitSnapshot.description,
        unidadesMedidaAdicionales: sanitizedUnits,
        categoria: productData.categoria,
        impuesto: productData.impuesto || '',
        afectacionTributariaId: productData.afectacionTributariaId,
        afectacionTributariaCodigo: productData.afectacionTributariaCodigo,
        afectacionTributariaCodigoTributo: productData.afectacionTributariaCodigoTributo,
        afectacionTributariaImpuesto: productData.afectacionTributariaImpuesto,
        afectacionTributariaNombre: productData.afectacionTributariaNombre,
        descripcion: productData.descripcion || '',
        establecimientoIds: finalEstablecimientoIds,
        disponibleEnTodos: false,
        alias: productData.alias || '',
        precioCompra: productData.precioCompra || 0,
        porcentajeGanancia: productData.porcentajeGanancia || 0,
        codigoBarras: normalizeBarcodeValue(productData.codigoBarras),
        codigoFabrica: productData.codigoFabrica || '',
        codigoSunat: productData.codigoSunat || '',
        codigoSunatNombre: productData.codigoSunatNombre || '',
        descuentoProducto: productData.descuentoProducto || 0,
        marca: productData.marca || '',
        modelo: productData.modelo || '',
        peso: productData.peso || 0,
        tipoExistencia: productData.tipoExistencia || 'MERCADERIAS',
        sujetoDetraccion: productData.esDetraccion ?? false,
        detraccionCodigo: productData.detraccionCodigo ?? null,
        detraccionId: productData.detraccionId ?? null,
        detraccionNombre: productData.detraccionNombre ?? null,
        detraccionPorcentaje: productData.detraccionPorcentaje ?? null,
        cuentaContableId: productData.cuentaContableId ?? null,
        cuentaContableCodigo: productData.cuentaContableCodigo ?? null,
        familiaId: productData.familiaId ?? null,
        familiaNombre: productData.familiaNombre ?? null,
        centroCostoId: productData.centroCostoId ?? null,
        centroCostoNombre: productData.centroCostoNombre ?? null,
        presupuestoId: productData.presupuestoId ?? null,
        presupuestoNombre: productData.presupuestoNombre ?? null,
        esIcbper: productData.esIcbper ?? false,
      });

      setImagePreview('');
      setPendingImageFiles([]);
      setAdditionalUnitErrors(sanitizedUnits.map(() => ({})));
      setProductType(productData.tipoProducto === 'SERVICIO' ? 'SERVICIO' : 'BIEN');
      setSelectedUnitFamily(inferredFamily);
      setErrors({});
      setUnitInfoMessage(null);
      setIsDescriptionExpanded(false);
    }, [activeEstablecimientoIds, defaultTaxLabel, sortedVisibleUnits, uniqueIds, resolveFamilyFromCode, resolveUnitSnapshot, resolveDefaultEnabledEstablecimientos]
  );

  useEffect(() => {
    if (!isOpen) {
      setHasInitializedForm(false);
      return;
    }

    if (product) {
      initializeFormFromProduct(product);
      setHasInitializedForm(true);
      return;
    }

    if (!hasInitializedForm) {
      initializeFormForNewProduct();
      setHasInitializedForm(true);
    }
  }, [
    isOpen,
    product,
    hasInitializedForm,
    initializeFormForNewProduct,
    initializeFormFromProduct
  ]);

  useEffect(() => {
    if (!isOpen || product) return;
    if (formData.establecimientoIds.length > 0) return;
    if (activeEstablecimientoIds.length === 0) return;
    
    const defaultIds = resolveDefaultEnabledEstablecimientos();
    if (defaultIds.length > 0) {
      setFormData(prev => ({
        ...prev,
        establecimientoIds: defaultIds
      }));
    }
  }, [isOpen, product, activeEstablecimientoIds, formData.establecimientoIds.length, resolveDefaultEnabledEstablecimientos]);

  useEffect(() => {
    if (!isOpen || !product?.id) {
      setArchivosExistentes([]);
      return;
    }
    
    let cancelled = false;
    archivosService.obtenerDeProducto(product.id)
      .then((response) => {
        if (cancelled) return;
        if (response.ok && response.data) {
          setArchivosExistentes(response.data.filter(a => a.mimeType.startsWith('image/')));
        }
      })
      .catch(() => {});
    
    return () => { cancelled = true; };
  }, [isOpen, product?.id]);

  const validateForm = useCallback(() => {
    const newErrors: FormError = {};
    const newAdditionalUnitErrors: AdditionalUnitError[] = [];

    if (isFieldVisible('nombre') && !formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (isFieldVisible('codigo')) {
      if (!formData.codigo.trim()) {
        newErrors.codigo = 'El código es requerido';
      } else {
        const codigoDuplicado = allProducts.find(
          p =>
            p.codigo.toLowerCase() === formData.codigo.trim().toLowerCase() &&
            p.id !== product?.id
        );
        if (codigoDuplicado) {
          newErrors.codigo = `El código "${formData.codigo}" ya existe en el producto "${codigoDuplicado.nombre}"`;
        }
      }
    }

    if (isFieldVisible('impuesto') && (!formData.impuesto || formData.impuesto.trim() === '')) {
      newErrors.impuesto = 'El impuesto es requerido';
    }

    if (isFieldVisible('unidad') && (!formData.unidad || formData.unidad.trim() === '')) {
      newErrors.unidad = 'La unidad de medida es requerida';
    }

    // Disponibilidad por establecimiento: no es requerida para guardar.

    if (isFieldVisible('categoria') && isFieldRequired('categoria') && !formData.categoria) {
      newErrors.categoria = 'La categoría es requerida';
    }

    if (isFieldVisible('descripcion') && isFieldRequired('descripcion') && !formData.descripcion?.trim()) {
      newErrors.descripcion = 'La descripción es requerida';
    }

    if (isFieldVisible('codigoBarras')) {
      const normalizedBarcode = normalizeBarcodeValue(formData.codigoBarras);
      const hasBarcodeValue = normalizedBarcode.length > 0;

      if (isFieldRequired('codigoBarras') && !hasBarcodeValue) {
        newErrors.codigoBarras = 'El código de barras es requerido';
      } else if (hasBarcodeValue && !isBarcodeValueValid(normalizedBarcode)) {
        newErrors.codigoBarras = `Ingresa ${BARCODE_MIN_LENGTH} a ${BARCODE_MAX_LENGTH} dígitos numéricos`;
      }
    }

    if (isFieldVisible('marca') && isFieldRequired('marca') && !formData.marca?.trim()) {
      newErrors.marca = 'La marca es requerida';
    }

    if (isFieldVisible('modelo') && isFieldRequired('modelo') && !formData.modelo?.trim()) {
      newErrors.modelo = 'El modelo es requerido';
    }

    if (isFieldVisible('peso') && isFieldRequired('peso') && formData.peso === 0) {
      newErrors.peso = 'El peso es requerido';
    }

    if (isFieldVisible('precioCompra') && isFieldRequired('precioCompra') && formData.precioCompra === 0) {
      newErrors.precioCompra = 'El precio de compra es requerido';
    }

    if (isFieldVisible('alias') && isFieldRequired('alias') && !formData.alias?.trim()) {
      newErrors.alias = 'El alias es requerido';
    }

    if (
      isFieldVisible('tipoExistencia') &&
      isFieldRequired('tipoExistencia') &&
      !formData.tipoExistencia
    ) {
      newErrors.tipoExistencia = 'El tipo de existencia es requerido';
    }

    const seenUnits = new Set<string>();
    formData.unidadesMedidaAdicionales.forEach((unit, index) => {
      const rowErrors: AdditionalUnitError = {};

      if (!unit.unidadCodigo) {
        rowErrors.unidad = 'Selecciona una unidad';
      } else {
        if (unit.unidadCodigo === formData.unidad) {
          rowErrors.unidad = 'No puede coincidir con la unidad base';
        } else if (seenUnits.has(unit.unidadCodigo)) {
          rowErrors.unidad = 'Unidad repetida';
        } else if (!isUsingFallbackUnits) {
          const resolved = availableUnits.find(item => item.code === unit.unidadCodigo);
          if (resolved && resolved.category !== selectedUnitFamily) {
            rowErrors.unidad = 'No coincide con la familia seleccionada';
          }
        }
        seenUnits.add(unit.unidadCodigo);
      }

      if (!unit.factorConversion || unit.factorConversion <= 0) {
        rowErrors.factor = 'El campo "Contiene" debe ser mayor a 0';
      }

      newAdditionalUnitErrors[index] = rowErrors;
    });

    setErrors(newErrors);
    setAdditionalUnitErrors(newAdditionalUnitErrors);

    const hasAdditionalErrors = newAdditionalUnitErrors.some(row => row.unidad || row.factor);
    return Object.keys(newErrors).length === 0 && !hasAdditionalErrors;
  }, [
    allProducts,
    formData,
    isFieldRequired,
    isFieldVisible,
    product,
    availableUnits,
    isUsingFallbackUnits,
    selectedUnitFamily
  ]);

  const handleSubmit = useCallback(
    async (event?: React.FormEvent | React.MouseEvent) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }

      if (!validateForm()) return null;

      const EstablecimientoIdsFromForm = formData.disponibleEnTodos
        ? activeEstablecimientoIds
        : uniqueIds(formData.establecimientoIds);

      const normalizedEstablecimientoIds =
        activeEstablecimientoIds.length === 1
          ? [activeEstablecimientoIds[0]]
          : EstablecimientoIdsFromForm.length > 0
            ? EstablecimientoIdsFromForm
            : resolveDefaultEnabledEstablecimientos();

      setLoading(true);
      try {
        const payload: ProductInput = {
          ...formData,
          imagen: typeof formData.imagen === 'string' ? formData.imagen : undefined,
          tipoProducto: productType,
          unitSymbol: formData.unidadSymbol,
          unitName: formData.unidadName,
          unidadMedidaDescripcion: formData.unidadMedidaDescripcion,
          establecimientoIds: normalizedEstablecimientoIds,
          disponibleEnTodos: false,
          esDetraccion: formData.sujetoDetraccion ?? false,
          detraccionCodigo: formData.detraccionCodigo ?? undefined,
          detraccionId: formData.detraccionId ?? undefined,
          detraccionNombre: formData.detraccionNombre ?? undefined,
          detraccionPorcentaje: formData.detraccionPorcentaje ?? undefined,
          cuentaContableId: formData.cuentaContableId ?? undefined,
          cuentaContableCodigo: formData.cuentaContableCodigo ?? undefined,
          familiaId: formData.familiaId ?? undefined,
          familiaNombre: formData.familiaNombre ?? undefined,
          centroCostoId: formData.centroCostoId ?? undefined,
          centroCostoNombre: formData.centroCostoNombre ?? undefined,
          presupuestoId: formData.presupuestoId ?? undefined,
          presupuestoNombre: formData.presupuestoNombre ?? undefined,
          esIcbper: formData.esIcbper ?? false,
        };

        const result = await Promise.resolve(onSave(payload));
        const savedId = (result as Product | undefined)?.id ?? product?.id;
        const savedProduct = result as Product | undefined;

        if (savedId && pendingImageFiles.length > 0) {
          await Promise.allSettled(
            pendingImageFiles.map((file, idx) =>
              archivosService.subirParaProducto(savedId, file, {
                esPrincipal: idx === 0,
                orden: idx,
              })
            )
          );
        }

        return savedProduct;
      } catch {
        return null;
      } finally {
        setLoading(false);
      }
    },
    [
      activeEstablecimientoIds,
      formData,
      pendingImageFiles,
      onSave,
      product,
      productType,
      resolveDefaultEnabledEstablecimientos,
      uniqueIds,
      validateForm
    ]
  );

  const handleImageUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImagePreview('');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFilesChange = useCallback((files: File[]) => {
    setPendingImageFiles(files);
  }, []);

  return {
    formData,
    setFormData,
    errors,
    additionalUnitErrors,
    loading,
    imagePreview,
    pendingImageFiles,
    archivosExistentes,
    productType,
    setProductType,
    handleProductTypeChange,
    selectedUnitFamily,
    isDescriptionExpanded,
    setIsDescriptionExpanded,
    unitInfoMessage,
    setUnitInfoMessage,
    showCategoryModal,
    setShowCategoryModal,
    filteredUnitsForFamily,
    unitFamilies,
    baseUnitOptions,
    isUsingFallbackUnits,
    remainingUnitsForAdditional,
    findUnitByCode,
    formatFactorValue,
    handleUnitFamilyChange,
    handleBaseUnitChange,
    addAdditionalUnit,
    removeAdditionalUnit,
    updateAdditionalUnit,
    getAdditionalUnitOptions,
    handleSubmit,
    handleImageUpload,
    handleFilesChange,
    initializeFormForNewProduct,
  };
};


